/* eslint-disable no-useless-escape */

import axios from 'axios';
import { InputNames, NotificationProps, NotificationVariants } from '@/types';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { string, object } from 'yup';
import { useState } from 'react';
import { useSiteMetadata } from '@/hooks/useSiteMetadata';

const noBlankSpacesRegEx = /(.|\s)*\S(.|\s)*/;
const telRegEx = /^(\+|00)?[1-9][0-9 \-\(\)\.]{7,32}$/;
const hubSpotURL =
  'https://api.hsforms.com/submissions/v3/integration/submit/43564565/135c7a12-d174-4c8c-8e7c-aa1fa6cd9566';

const schema = object({
  [InputNames.FIRST_NAME]: string().required().matches(noBlankSpacesRegEx),
  [InputNames.LAST_NAME]: string().required().matches(noBlankSpacesRegEx),
  [InputNames.EMAIL]: string().email().required(),
  [InputNames.MOBILE_PHONE]: string().test('Check RegExp', (value) =>
    value ? value.match(telRegEx) : true
  ),
  [InputNames.COMPANY]: string(),
  [InputNames.MESSAGE]: string()
});

export const useContactForm = () => {
  const { siteUrl } = useSiteMetadata();

  const requestBody = {
    fields: [],
    context: {
      pageUri: siteUrl,
      pageName: 'Airstage Cloud',
      pageId: 'pageID'
    }
  };
  const [notification, setNotification] = useState<NotificationProps | null>(
    null
  );
  const { register, formState, handleSubmit, getValues, reset } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const clearNotification = (
    responseType: NotificationVariants = NotificationVariants.SUCCESS
  ) => {
    setNotification(null);
    if (responseType === NotificationVariants.SUCCESS) reset();
  };

  const mapFieldsToBody = () => {
    requestBody.fields = [];
    const formValues = getValues();

    for (const name in formValues) {
      if (Object.prototype.hasOwnProperty.call(formValues, name)) {
        const value = formValues[name];
        requestBody.fields.push({ name, value });
      }
    }

    requestBody.fields.push({
      name: 'lead_source',
      value: siteUrl
    });
  };

  const onSubmit = async () => {
    mapFieldsToBody();
    try {
      const response = await axios.post(hubSpotURL, requestBody);
      if (response.status === 200) {
        setNotification({
          title: 'Message Received',
          message:
            'Thanks for your interest in AIRSTAGE Cloud! Our team will be in touch shortly.',
          variant: NotificationVariants.SUCCESS
        });
        setTimeout(() => {
          clearNotification();
        }, 3000);
      } else {
        setNotification({
          title: 'Unable to submit',
          message:
            'There was a problem with your submission. Please review and try again. ',
          variant: NotificationVariants.ERROR
        });
      }
    } catch {
      setNotification({
        title: 'Unable to submit',
        message:
          'There was a problem with your submission. Please review and try again.',
        variant: NotificationVariants.ERROR
      });
    }
  };

  return {
    handleSubmit: (event: Event) => {
      event.preventDefault();
      handleSubmit(onSubmit)();
    },
    register,
    clearNotification,
    formState,
    notification
  };
};
